// Phase 2D.2A — the single source of truth for the OfferTrace password policy.
//
// Every rule is returned SEPARATELY so the UI can render the five requirements
// individually (registration and password reset use the very same helper).
// This module is intentionally dependency-free and side-effect-free: it must be
// verifiable in isolation and must never touch storage, the network or logging.
//
// Rules (identical to the policy configured in Supabase Auth):
//   length     — at least 8 characters
//   lowercase  — at least one a-z
//   uppercase  — at least one A-Z
//   digit      — at least one 0-9
//   special    — at least one ASCII punctuation character. A space never counts,
//                and neither does a letter from a non-Latin alphabet, so a
//                password accepted here is also accepted by the symbol
//                requirement configured in Supabase Auth.

export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_RULE_IDS = Object.freeze([
  "length",
  "lowercase",
  "uppercase",
  "digit",
  "special",
]);

const LOWERCASE_RE = /[a-z]/;
const UPPERCASE_RE = /[A-Z]/;
const DIGIT_RE = /[0-9]/;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;

// Every printable ASCII punctuation character — and nothing else. The space is
// deliberately absent.
const SPECIAL_CHARS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

function hasSpecial(password) {
  for (let i = 0; i < password.length; i += 1) {
    if (SPECIAL_CHARS.indexOf(password.charAt(i)) !== -1) return true;
  }
  return false;
}

export function evaluatePassword(value) {
  const password = typeof value === "string" ? value : "";
  const rules = {
    length: password.length >= PASSWORD_MIN_LENGTH,
    lowercase: LOWERCASE_RE.test(password),
    uppercase: UPPERCASE_RE.test(password),
    digit: DIGIT_RE.test(password),
    special: hasSpecial(password),
  };
  return {
    rules,
    valid: PASSWORD_RULE_IDS.every((id) => rules[id] === true),
  };
}

export function passwordsMatch(value, confirmation) {
  const a = typeof value === "string" ? value : "";
  const b = typeof confirmation === "string" ? confirmation : "";
  return a.length > 0 && a === b;
}

export function isValidEmail(value) {
  const email = typeof value === "string" ? value.trim() : "";
  return email.length > 0 && email.length <= 254 && EMAIL_RE.test(email);
}
